/**
 * Client-side room-name slugify (CR-26): the registration accepts arbitrary user names -
 * spaces, mixed case, Cyrillic - and this module computes the registered slug the same way the
 * control service does BEFORE the create/availability call, so the user sees the actual address
 * they will get. The transliteration table is shared with the Go implementation
 * (control/internal/httpapi/slugify.go) and the two sides are pinned to the SAME fixture list:
 * web/src/lib/slugify.test.ts and the Go test run identical expectations - change both sides
 * together or the client preview and the registered slug drift apart.
 */

/** Slug shape bounds of the slugify pipeline, mirroring the server's constants. */
export const MAX_SLUG_RUNES = 64;
export const MIN_SLUG_RUNES = 3;

/**
 * The RU→EN transliteration table (lowercase Russian alphabet): ё folds to e, й to y, the hard
 * and soft signs drop, and the digraph letters transliterate fully (ж zh, х kh, ц ts, ч ch, ш sh,
 * щ shch, ю yu, я ya; ы y, э e) - the common translit.ru style the server pins.
 */
const TRANSLIT_TABLE: Readonly<Record<string, string>> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

/**
 * Converts an arbitrary user-typed room name into the slug the registry registers: each rune is
 * lowercased, Cyrillic transliterates through the table, plain ASCII letters and digits survive,
 * and every other rune (spaces, punctuation, any other script) collapses into a single hyphen;
 * hyphen runs merge and the edges trim. The result is clamped to MAX_SLUG_RUNES (the trailing
 * hyphen a truncation can leave is trimmed again). Returns null when fewer than
 * {@link MIN_SLUG_RUNES} characters survive - the server refuses such names with 400, so the
 * caller can reject them before any request.
 */
export function slugifyRoomName(name: string): string | null {
  const lowered = name.toLowerCase();
  let out = '';
  for (const char of lowered) {
    const mapped = TRANSLIT_TABLE[char];
    if (mapped !== undefined) {
      out += mapped;
    } else if ((char >= 'a' && char <= 'z') || (char >= '0' && char <= '9')) {
      out += char;
    } else {
      out += '-';
    }
  }
  const collapsed = out
    .split('-')
    .filter((part) => part.length > 0)
    .join('-');
  const clamped = collapsed.length > MAX_SLUG_RUNES ? collapsed.slice(0, MAX_SLUG_RUNES).replace(/-+$/, '') : collapsed;
  if (clamped.length < MIN_SLUG_RUNES) {
    return null;
  }
  return clamped;
}
